/**
 * Axis-aligned rectangle. Unless stated otherwise, boxes are stored in
 * **normalized** coordinates (0..1) relative to the displayed source frame, so they
 * are independent of the resolution an analysis ran at.
 */
export interface Box {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export const box = (x: number, y: number, w: number, h: number): Box => ({ x, y, w, h });

export const boxArea = (b: Box): number => Math.max(0, b.w) * Math.max(0, b.h);

export const boxCenter = (b: Box): { x: number; y: number } => ({ x: b.x + b.w / 2, y: b.y + b.h / 2 });

export function intersectionOverUnion(a: Box, b: Box): number {
  const ix = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
  const iy = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
  const intersection = ix * iy;
  const union = boxArea(a) + boxArea(b) - intersection;
  return union > 0 ? intersection / union : 0;
}

/** Grows a box around its center by `factor` (1.2 = 20 % larger). */
export function scaleBox(b: Box, factor: number): Box {
  const w = b.w * factor;
  const h = b.h * factor;
  return box(b.x + (b.w - w) / 2, b.y + (b.h - h) / 2, w, h);
}

/** Clamps a box to `[0, maxW] × [0, maxH]`; returns null when nothing is left. */
export function clampBox(b: Box, maxW = 1, maxH = 1): Box | null {
  const x1 = Math.max(0, b.x);
  const y1 = Math.max(0, b.y);
  const x2 = Math.min(maxW, b.x + b.w);
  const y2 = Math.min(maxH, b.y + b.h);
  return x2 > x1 && y2 > y1 ? box(x1, y1, x2 - x1, y2 - y1) : null;
}

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

export const lerpBox = (a: Box, b: Box, t: number): Box =>
  box(lerp(a.x, b.x, t), lerp(a.y, b.y, t), lerp(a.w, b.w, t), lerp(a.h, b.h, t));

/** Distance between centers relative to the larger box dimension (scale independent). */
export function relativeCenterDistance(a: Box, b: Box): number {
  const ca = boxCenter(a);
  const cb = boxCenter(b);
  const scale = Math.max(a.w, a.h, b.w, b.h, 1e-9);
  return Math.hypot(ca.x - cb.x, ca.y - cb.y) / scale;
}

/** Maps a box from one pixel space to another (e.g. analysis size → normalized). */
export const scaleBoxAxes = (b: Box, sx: number, sy: number): Box => box(b.x * sx, b.y * sy, b.w * sx, b.h * sy);
